import { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Button, Grid, Typography, Avatar, Badge, Link, useScrollTrigger,
  Box, IconButton, FilledInput
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import PersonIcon from '@mui/icons-material/Person';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import moment from 'moment';
import 'moment/locale/es';

import { urlImg } from '../common/helpers';

import { useSelector, useDispatch } from 'react-redux';
import NotificacionesMenu from './NotificacionesMenu';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: '#F9FAFB',
  margin: 'auto',
  width: '100%',
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1
}));

const SearchClear = styled('div')(({ theme }) => ({
  top: 0,
  right: 0,
  height: '100%',
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingRight: 10,
  cursor: 'pointer',
}));

const Header = ({
  window, changeMenu
}) => {
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);
  const user = useSelector(state => state.user.user);
  const { notifications, unreadCount } = useSelector(state => state.notifications);
  const [notiOpen, setNotiOpen] = useState(null);
  const [buscar, setBuscar] = useState("");

  const navigate = useNavigate();
  moment.locale('es');
  const triggerSearch = useScrollTrigger({
    target: window ? window() : undefined,
    disableHysteresis: true,
    threshold: 400,
  });

  const handleClickNot = (event) => {
    setNotiOpen(event.currentTarget);
  };

  const handleCloseNot = () => {
    setNotiOpen(null);
  };

  const login = () => {
    navigate('/')
  }

  const irA = donde => () => {
    navigate(donde);
  }

  const submitSearch = () => {
    const q = buscar.trim();
    navigate(q ? `/buscar?q=${encodeURIComponent(q)}` : '/buscar');
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitSearch();
    }
  };

  let bLogin = user && auth.isAuthenticated ? (
    <>
      <Button aria-controls="notificaciones" aria-haspopup="true" onClick={handleClickNot}>
        {
          unreadCount > 0 ? (
            <Badge badgeContent={unreadCount} color="primary">
              <NotificationsNoneOutlinedIcon />
            </Badge>
          ) : (
            <NotificationsNoneOutlinedIcon />
          )
        }
      </Button>
      <NotificacionesMenu
        user={user}
        auth={auth}
        notiOpen={notiOpen}
        handleCloseNot={handleCloseNot}
        notifications={notifications}
        navigate={navigate}
      />

      {/* Antes era un <Button> (con ripple/hover) que no tenía onClick: parecía
          clickable pero no hacía nada. Ahora es un Box puramente informativo. */}
      <Box className="menu-header-icon" sx={{ display: 'flex', alignItems: 'center' }}>
        <Avatar src={`${urlImg}/w_55,h_55,c_fill/v${user.versionAvatar}/users/${user._id}/${user.avatar}`} sx={{ width: 55, height: 55 }} />
        {/* El nombre se oculta en pantallas chicas (se recortaba); solo avatar en móvil */}
        <Typography sx={{ display: { xs: 'none', md: 'block' } }}>{user.name}<br /><small>Usuario</small></Typography>
      </Box>
    </>
  ) : (
    <>
      <Button className="btn btn-blue" onClick={login}>
        <PersonIcon className="icon-btn" />
        <span>Iniciar sesión</span>
      </Button>
      <Button className="btn btn-white ml-2" onClick={irA("/signup")}>
        <HowToRegIcon className="icon-btn" />
        <span>Regístrate</span>
      </Button>
    </>
  );

  // Acciones del header MÓVIL: solo la campana (sin avatar) o login/registro.
  // La campana comparte el mismo menú (NotificacionesMenu) que la versión de escritorio.
  const accionesMovil = user && auth.isAuthenticated ? (
    <IconButton
      aria-controls="notificaciones"
      aria-haspopup="true"
      color="inherit"
      onClick={handleClickNot}
      sx={{ flexShrink: 0 }}
    >
      {unreadCount > 0 ? (
        <Badge badgeContent={unreadCount} color="primary"><NotificationsNoneOutlinedIcon /></Badge>
      ) : (
        <NotificationsNoneOutlinedIcon />
      )}
    </IconButton>
  ) : (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
      <Button className="btn btn-blue" onClick={login}>
        <PersonIcon className="icon-btn" />
        <span>Iniciar sesión</span>
      </Button>
      <Button className="btn btn-white" onClick={irA("/signup")}>
        <HowToRegIcon className="icon-btn" />
        <span>Regístrate</span>
      </Button>
    </Box>
  );

  return (
    <AppBar
      variant="elevation"
      elevation={0}
      color="inherit"
      position="fixed"
      className={`navbar navbarScrolled
        ${!triggerSearch ? "" : "searchScrolled"}
      `}
    >
      {/* Versión móvil — barra compacta SIEMPRE visible en una fila:
          menú + logo + buscador (a lado del logo) + campana. Sin avatar. */}
      <Toolbar disableGutters sx={{ display: { xs: 'block', md: 'none' } }}>
        <Box sx={{ width: '100%', pl: 1.5, pr: 2.5, py: 1, display: 'flex', alignItems: 'center', gap: 1.25 }}>
          {user && auth.isAuthenticated &&
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={changeMenu}
              sx={{ flexShrink: 0}}
            >
              <MenuIcon />
            </IconButton>
          }
          <Link className="link-movil" href="/home" sx={{ flexShrink: 0 }}>
            {/* Logo simplificado (solo ícono) para ganar espacio al buscador en móvil */}
            <img alt="oneService" src="/logo3.png" style={{ height: 32, width: 'auto' }} />
          </Link>
          <Search sx={{ flexGrow: 1, minWidth: 0 }}>
            <SearchIconWrapper onClick={submitSearch} sx={{ cursor: 'pointer' }}>
              <SearchIcon fontSize="small" color="primary" />
            </SearchIconWrapper>
            <FilledInput
              className="input-search"
              fullWidth
              id="search-movil"
              name="search"
              value={buscar}
              onChange={(e) => setBuscar(e.target.value)}
              onKeyDown={onSearchKeyDown}
              placeholder="Buscar servicios"
            />
            {buscar !== "" &&
              <SearchClear onClick={() => setBuscar("")}>
                <Typography>Borrar</Typography>
              </SearchClear>
            }
          </Search>
          {accionesMovil}
        </Box>
      </Toolbar>


      {/* Versión web */}
      <Box sx={{ flexGrow: 1, margin: 1, display: { xs: 'none', md: 'block' } }}>
        <Grid className="navbar-content" container spacing={3}>
          <Grid
            container
            item
            size={3}
            sx={{
              justifyContent: "flex-start",
              alignItems: "center"
            }}
          >
            {
              user && auth.isAuthenticated &&
              <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                sx={{ mr: 2 }}
                onClick={changeMenu}
              >
                <MenuIcon />
              </IconButton>
            }
            <Link href="/home">
              <img className="logo" alt="logo" src="/logo.png" />
            </Link>
          </Grid>
          <Grid
            className="content-center"
            size={{ xs: 5, md: 6, lg: 6 }}
          >
            <Search>
              <SearchIconWrapper onClick={submitSearch} sx={{ cursor: 'pointer' }}>
                <SearchIcon fontSize="small" color='primary' />
              </SearchIconWrapper>
              <FilledInput
                className="input-search"
                fullWidth
                id="search"
                label="search"
                name="search"
                value={buscar}
                onChange={(e) => setBuscar(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder="¿Qué servicio estás buscando?"
              />
              {buscar !== "" &&
                <SearchClear onClick={() => setBuscar("")}>
                  <Typography>Borrar</Typography>
                </SearchClear>
              }
            </Search>
          </Grid>
          <Grid
            className="content-web-actions"
            size={{ xs: 4, md: 3, lg: 3 }}
          >
            {bLogin}
          </Grid>
        </Grid>
      </Box>
    </AppBar>
  )
}

export default Header;